<template>
  <div class="min-h-screen flex flex-col">
    <!-- Navigation for authenticated users -->
    <nav v-if="$page.props.auth?.user" class="bg-white border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <!-- Logo/Title -->
          <div class="flex-shrink-0">
            <a href="/" class="text-lg font-semibold text-gray-900">Kiosk</a>
          </div>

          <!-- Desktop navigation -->
          <div class="hidden md:flex md:items-center md:gap-4">
            <a href="/invites" class="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md">
              {{ tl('common.requests') }}
            </a>
            <a href="/profile" class="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md">
              {{ tl('nav.profile') }}
            </a>
            <a href="/appointments" class="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md">
              {{ tl('nav.appointments') }}
            </a>
            <a href="/help" class="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md">
              {{ tl('nav.help') }}
            </a>
            <LanguageSelector />
            <button @click="handleLogout" class="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md flex items-center gap-1">
              <LogOut class="h-4 w-4" />
              {{ tl('nav.logout') }}
            </button>
          </div>

          <!-- Mobile menu button -->
          <div class="md:hidden">
            <button
              @click="mobileMenuOpen = !mobileMenuOpen"
              class="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <span class="sr-only">Open main menu</span>
              <svg v-if="!mobileMenuOpen" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg v-else class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile menu slide-in -->
      <Transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 -translate-x-full"
        enter-to-class="opacity-100 translate-x-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-x-0"
        leave-to-class="opacity-0 -translate-x-full"
      >
        <div v-if="mobileMenuOpen" class="md:hidden">
          <div class="fixed inset-0 z-40" @click="mobileMenuOpen = false">
            <div class="fixed inset-0 bg-gray-600 bg-opacity-75"></div>
          </div>
          <div class="fixed inset-y-0 left-0 max-w-xs w-full bg-white shadow-xl z-50">
            <div class="flex items-center justify-between px-4 py-4 border-b">
              <span class="text-lg font-semibold text-gray-900">Menu</span>
              <button @click="mobileMenuOpen = false" class="p-2 rounded-md text-gray-700 hover:bg-gray-100">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="px-2 pt-2 pb-3 space-y-1">
              <a href="/invites" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                {{ tl('common.requests') }}
              </a>
              <a href="/profile" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                {{ tl('nav.profile') }}
              </a>
              <a href="/appointments" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                {{ tl('nav.appointments') }}
              </a>
              <a href="/help" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                {{ tl('nav.help') }}
              </a>
              <div class="px-3 py-2">
                <LanguageSelector />
              </div>
              <button @click="handleLogout" class="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 flex items-center gap-2">
                <LogOut class="h-4 w-4" />
                {{ tl('nav.logout') }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </nav>

    <!-- Navigation for unauthenticated users -->
    <nav v-else class="bg-white border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <a href="/" class="text-lg font-semibold text-gray-900">Kiosk</a>
          <div class="flex items-center gap-3">
            <a href="/help" class="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md">
              {{ tl('nav.help') }}
            </a>
            <LanguageSelector />
          </div>
        </div>
      </div>
    </nav>

    <FlashMessage />

    <slot />
  </div>
</template>

<script setup>
import { router, usePage } from '@inertiajs/vue3'
import { LogOut } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import { FlashMessage } from '@/components/ui/flash-message'
import LanguageSelector from '@/components/ui/LanguageSelector.vue'
import { t } from '@/entrypoints/translations.js'

// Get current locale from page props
const page = usePage()
const currentLocale = computed(() => page.props.locale?.current || 'en')

// Mobile menu state
const mobileMenuOpen = ref(false)

// Locale-aware translation function
const tl = (key) => t(key, { locale: currentLocale.value })

// Logout handler with confirmation
const handleLogout = () => {
  if (confirm(tl('auth.confirm_logout'))) {
    mobileMenuOpen.value = false
    router.delete('/users/logout')
  }
}
</script>
